import { Injectable } from '@angular/core';
import makeDebug from 'src/makeDebug';
import { IndexedDbService } from './indexed-db.service';

const debug = makeDebug('cache:attachment');

/**
 * Service for caching attachments using IndexedDB.
 *
 * Features:
 * - Uses a percentage-based approach for storage management (80% of free quota)
 * - Hard limit of 200MB maximum cache size even if more quota is available
 * - Implements LRU (Least Recently Used) eviction strategy based on size
 * - Automatic quota detection using navigator.storage API
 * - Fallback mechanisms:
 *   1. If quota detection fails, uses a 50MB default allowance
 *   2. If size-based eviction fails, falls back to count-based eviction (20 items)
 *   3. If errors occur calculating storage, uses conservative limits
 * - 4. If retrival of attachment from cache fails, falls back to download from server
 */
@Injectable({ providedIn: 'root' })
export class AttachmentCacheService {
  /**
   * This cache is currently only used in web
   * to cache attachments that are downloaded
   * from the server.
   */
  private readonly DB_NAME = 'alberta-cache-db';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'attachments';
  // Maximum percentage of available quota to use (default: 80%)
  private readonly maxQuotaPercentage = 80;
  // Hard limit on maximum cache size in bytes (200MB)
  private readonly absoluteMaxCacheSize = 200 * 1024 * 1024;
  // Fallback max items when quota information is not available
  private readonly fallbackMaxCacheItems = 20;
  // Default quota estimate when quota info is not available
  private readonly defaultQuotaEstimate = 50 * 1024 * 1024; // 50MB
  private dbInitialized = false;
  // Current cache size in bytes
  private currentCacheSize = 0;

  constructor(private indexedDbService: IndexedDbService) {
    this.initializeDb();
  }

  private async initializeDb(): Promise<void> {
    try {
      await this.indexedDbService.initDatabase(this.DB_NAME, this.DB_VERSION, db => {
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          const store = db.createObjectStore(this.STORE_NAME, { keyPath: 'id' });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('size', 'size', { unique: false });
          store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }
      });
      this.dbInitialized = true;
      await this.calculateCurrentCacheSize();
    } catch (error) {
      window.logger.error('Failed to initialize database:', error);
      throw error;
    }
  }

  /**
   * Calculate the current total size of the cache
   */
  private async calculateCurrentCacheSize(): Promise<number> {
    try {
      // Use getAllKeys to get all items
      const items = await this.indexedDbService.performTransaction(this.STORE_NAME, 'readonly', store =>
        store.getAll()
      );

      let totalSize = 0;
      if (items && Array.isArray(items)) {
        // Sum up the sizes of all items
        totalSize = items.reduce((sum, item) => sum + (item.size || 0), 0);
      }

      this.currentCacheSize = totalSize;
      debug(`Calculated current cache size: ${totalSize} bytes`);
      return totalSize;
    } catch (error) {
      window.logger.error('Failed to calculate cache size:', error);
      return 0;
    }
  }

  /**
   * Cache an attachment
   */
  async cacheAttachment(userId: string, attachmentId: string, blob: Blob): Promise<void> {
    if (!this.dbInitialized) {
      await this.initializeDb();
    }

    const now = Date.now();
    const cacheId = this.getCacheKey(userId, attachmentId);

    try {
      await this.enforceLRULimit();

      await this.indexedDbService.performTransaction(this.STORE_NAME, 'readwrite', store =>
        store.put({
          id: cacheId,
          userId,
          attachmentId,
          blob,
          timestamp: now,
          size: blob.size,
          accessCount: 0,
          lastAccessed: now,
        })
      );
    } catch (error) {
      window.logger.error('Failed to cache attachment:', error);
      throw error;
    }
  }

  /**
   * Retrieve a cached attachment
   */
  async getCachedAttachment(userId: string, attachmentId: string): Promise<Blob | null> {
    if (!this.dbInitialized) {
      await this.initializeDb();
    }

    const cacheId = this.getCacheKey(userId, attachmentId);

    try {
      const item = await this.indexedDbService.performTransaction(this.STORE_NAME, 'readonly', store =>
        store.get(cacheId)
      );

      if (!item) {
        return null;
      }

      const now = Date.now();
      await this.indexedDbService.performTransaction(this.STORE_NAME, 'readwrite', store =>
        store.put({
          ...item,
          lastAccessed: now,
          accessCount: (item.accessCount || 0) + 1,
        })
      );

      return item.blob;
    } catch (error) {
      window.logger.error('Failed to get cached attachment:', error);
      throw error;
    }
  }

  /**
   * Delete a cached attachment
   */
  async deleteCachedAttachment(userId: string, attachmentId: string): Promise<void> {
    if (!this.dbInitialized) {
      await this.initializeDb();
    }

    const cacheId = this.getCacheKey(userId, attachmentId);

    try {
      await this.indexedDbService.performTransaction(this.STORE_NAME, 'readwrite', store => store.delete(cacheId));
    } catch (error) {
      window.logger.error('Failed to delete cached attachment:', error);
      throw error;
    }
  }

  /**
   * Check storage quota and return the maximum allowed size in bytes
   * Applies two limits:
   * 1. maxQuotaPercentage% of available storage
   * 2. absoluteMaxCacheSize as a hard limit (200MB)
   * Returns the smaller of the two values
   */
  private async getMaxAllowedSize(): Promise<number> {
    try {
      // Try to use the Storage API to get quota information
      if (navigator.storage && navigator.storage.estimate) {
        const { usage, quota } = await navigator.storage.estimate();

        if (usage !== undefined && quota) {
          const availableSpace = quota - (usage || 0);
          // Calculate percentage-based limit
          const percentageBased = availableSpace * (this.maxQuotaPercentage / 100);
          // Take the smaller of percentage-based or absolute maximum
          const maxAllowedSize = Math.min(percentageBased, this.absoluteMaxCacheSize);

          debug(
            `Storage quota: ${this.formatBytes(quota)}, Used: ${this.formatBytes(usage)}, Available: ${this.formatBytes(availableSpace)}`
          );
          debug(
            `Max allowed cache size: ${this.formatBytes(maxAllowedSize)} (${this.maxQuotaPercentage}% of free space with ${this.formatBytes(this.absoluteMaxCacheSize)} cap)`
          );

          return maxAllowedSize;
        }
      }

      // Fallback to a conservative estimate if quota information is not available
      console.warn('Storage quota information not available, using fallback limit');
      return Math.min(this.defaultQuotaEstimate, this.absoluteMaxCacheSize); // Use default with hard cap
    } catch (error) {
      window.logger.error('Error getting storage quota:', error);
      return 10 * 1024 * 1024; // 10MB as conservative fallback on error
    }
  }

  /**
   * Enforce the storage quota limit based on percentage
   */
  private async enforceLRULimit(): Promise<void> {
    try {
      // Update the current cache size
      await this.calculateCurrentCacheSize();

      // Get maximum allowed size
      const maxAllowedSize = await this.getMaxAllowedSize();

      debug(`Current cache size: ${this.currentCacheSize} bytes, Max allowed: ${maxAllowedSize} bytes`);

      if (this.currentCacheSize > maxAllowedSize) {
        // We need to free up some space
        const excessSize = this.currentCacheSize - maxAllowedSize;
        const percentageToRemove = Math.min(30, Math.ceil((excessSize / this.currentCacheSize) * 100));

        await this.clearLeastRecentlyUsedBySize(percentageToRemove);
      }
    } catch (error) {
      window.logger.error('Failed to enforce LRU limit:', error);

      // Fallback to number of items in cache mechanism if there's an error
      try {
        const count = await this.indexedDbService.performTransaction(this.STORE_NAME, 'readonly', store =>
          store.count()
        );

        if (count >= this.fallbackMaxCacheItems) {
          const itemsToRemove = Math.max(1, count - this.fallbackMaxCacheItems + 1);
          await this.clearLeastRecentlyUsed(itemsToRemove);
        }
      } catch (fallbackError) {
        window.logger.error('Failed to enforce fallback LRU limit:', fallbackError);
        throw error; // Throw the original error
      }
    }
  }

  /**
   * Clear least recently used items
   * This is a fixed implementation that properly handles async operations
   */
  private async clearLeastRecentlyUsed(count: number): Promise<void> {
    // Get the keys of the least recently used items
    const keys: IDBValidKey[] = await this.indexedDbService.performTransaction(this.STORE_NAME, 'readonly', store => {
      const index = store.index('lastAccessed');
      // Get the first `count` keys (least recently used)
      return index.getAllKeys(null, count);
    });

    // Delete those items
    await this.deleteItems(keys as string[]);
  }

  /**
   * Clear least recently used items based on percentage of total size
   * @param percentageToRemove Percentage of current cache size to remove
   */
  private async clearLeastRecentlyUsedBySize(percentageToRemove: number): Promise<void> {
    try {
      // Get all items first
      const items = (await this.indexedDbService.performTransaction(this.STORE_NAME, 'readonly', store =>
        store.getAll()
      )) as Array<{
        id: string;
        size: number;
        lastAccessed: number;
      }>;

      if (!items || !Array.isArray(items)) {
        // If no items or wrong format, fall back to count-based approach
        await this.clearLeastRecentlyUsed(5);
        return;
      }

      // Sort by lastAccessed (oldest first)
      items.sort((a, b) => (a.lastAccessed || 0) - (b.lastAccessed || 0));

      // Calculate how much space we need to free up
      const targetSizeToRemove = (this.currentCacheSize * percentageToRemove) / 100;
      let accumulatedSize = 0;
      const keysToRemove: string[] = [];

      // Add items to the removal list until we reach the target size
      for (const item of items) {
        if (accumulatedSize >= targetSizeToRemove) {
          break;
        }

        keysToRemove.push(item.id);
        accumulatedSize += item.size || 0;
      }

      if (keysToRemove.length === 0) {
        debug('No items need to be removed.');
        return;
      }

      debug(`Clearing ${keysToRemove.length} items to free up ~${accumulatedSize} bytes`);

      // Delete the selected items
      await this.deleteItems(keysToRemove);

      // Update cache size after deletion
      this.currentCacheSize = Math.max(0, this.currentCacheSize - accumulatedSize);
    } catch (error) {
      window.logger.error('Error clearing items by size:', error);
      // Fallback to the standard count-based approach
      await this.clearLeastRecentlyUsed(5);
    }
  }

  /**
   * Helper method to delete multiple items
   */
  private async deleteItems(keys: string[]): Promise<void> {
    if (keys.length === 0) return;

    return this.indexedDbService.performTransaction(this.STORE_NAME, 'readwrite', store => {
      // Create an array to store all requests
      const deleteRequests: IDBRequest[] = [];

      // Delete all items
      for (const key of keys) {
        const request = store.delete(key);
        deleteRequests.push(request);
      }

      // Return the last request for transaction completion
      return deleteRequests[deleteRequests.length - 1];
    });
  }

  /**
   * Format bytes to a human readable format
   * @param bytes Number of bytes to format
   * @param decimals Number of decimal places to show
   * @returns Formatted string with appropriate unit suffix
   */
  private formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Generate a cache key
   */
  private getCacheKey(userId: string, attachmentId: string): string {
    return `${userId}/attachment/${attachmentId}`;
  }
}
